import { useState, useEffect, useCallback } from 'react';
import { apiClient } from '../services/api';

interface UseApiState<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
}

interface UseApiOptions {
  immediate?: boolean;
  onSuccess?: (data: any) => void;
  onError?: (error: string) => void;
}

export function useApi<T>(
  endpoint: string, 
  options: UseApiOptions = {}
): UseApiState<T> & { refetch: () => Promise<void> } {
  const { immediate = true, onSuccess, onError } = options;
  
  const [state, setState] = useState<UseApiState<T>>({
    data: null,
    loading: immediate && !!endpoint,
    error: null,
  });

  const fetchData = useCallback(async () => {
    if (!endpoint) {
      setState({
        data: null,
        loading: false,
        error: null,
      });
      return;
    }

    try {
      setState(prev => ({ ...prev, loading: true, error: null }));
      
      const response = await apiClient.get(endpoint);
      const data = response.data;
      
      setState({
        data,
        loading: false,
        error: null,
      });
      
      if (onSuccess) {
        onSuccess(data);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An error occurred';
      
      setState({
        data: null,
        loading: false,
        error: errorMessage,
      });
      
      if (onError) {
        onError(errorMessage);
      }
    }
  }, [endpoint, onSuccess, onError]);

  useEffect(() => {
    if (immediate && endpoint) {
      fetchData();
    }
  }, [fetchData, immediate, endpoint]);

  return {
    ...state,
    refetch: fetchData,
  };
}

// Specialized hook for pages
export function usePage(slug: string) {
  return useApi(`/pages/${slug}.json`);
}

// Specialized hook for content pages
export function useContentPage(slug: string) {
  return useApi(`/pages/content/${slug}.json`);
}

export default useApi;
